if ( typeof ShippingNotice !== 'function' ) {

  class ShippingNotice extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      // Render with whatever data-cart-total Liquid set on the server.
      this.init();

      // Refresh in real-time whenever the cart changes. We bind a few possible
      // event names because the theme uses several conventions:
      //   - `cart-updated` is dispatched on the <cart-form> element (no bubble)
      //   - `krown:cart:update` / `krown:cart:add` are KROWN-dispatcher events
      //   - `cart:updated` is what our own JTL refresh fires after section swap.
      // Re-fetch /cart.js so we always read the canonical cart.total_price
      // (post-discounts), not the rendered subtotal.
      if (!this._listenersBound) {
        this._listenersBound = true;
        this._onCartUpdate = () => this.refreshFromCartJson();

        document.addEventListener('cart-updated', this._onCartUpdate);
        document.addEventListener('cart:updated', this._onCartUpdate);
        document.addEventListener('krown:cart:update', this._onCartUpdate);
        document.addEventListener('krown:cart:add', this._onCartUpdate);

        // cart-updated on <cart-form> doesn't bubble — listen directly there.
        document.querySelectorAll('cart-form').forEach((cf) => {
          cf.addEventListener('cart-updated', this._onCartUpdate);
        });
      }
    }

    disconnectedCallback() {
      if (this._onCartUpdate) {
        document.removeEventListener('cart-updated', this._onCartUpdate);
        document.removeEventListener('cart:updated', this._onCartUpdate);
        document.removeEventListener('krown:cart:update', this._onCartUpdate);
        document.removeEventListener('krown:cart:add', this._onCartUpdate);
        document.querySelectorAll('cart-form').forEach((cf) => {
          cf.removeEventListener('cart-updated', this._onCartUpdate);
        });
      }
    }

    refreshFromCartJson() {
      // Bucketed bars (nt-free-ship-bar.liquid) read their own share of the cart from the
      // Liquid helper section — /cart.js has no tags, so it can't split blanks from supplies.
      if (this.dataset.bucket) {
        const key = this.dataset.bucket;
        ShippingNotice.fetchBuckets().then((b) => {
          if (!b) return;
          this.setAttribute('data-cart-total', b[key + 'Total']);
          const row = this.closest('[data-nt-fs-row]');
          if (row) {
            // Transfers & Supplies keeps showing on a blanks-free cart (as before); a bucket
            // with no lines hides only when the other bucket has something to show.
            const other = key === 'blanks' ? 'other' : 'blanks';
            row.hidden = !(b[key + 'Count'] > 0) && (key === 'blanks' || b[other + 'Count'] > 0);
          }
          this.init();
        });
        return;
      }
      fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then((res) => res.json())
        .then((cart) => {
          // cart.total_price is the post-discount cart total (in cents) —
          // that's what we count toward free shipping.
          this.setAttribute('data-cart-total', cart.total_price);
          this.init();
        })
        .catch(() => { /* swallow — keep the last rendered state */ });
    }

    init() {

      const textEl = this.querySelector('[data-js-free-shipping-text]');
      if (!textEl) return;

      const freeShippingThreshold = Math.round(Number(this.getAttribute('data-free-shipping')) * (Shopify.currency.rate ? Number(Shopify.currency.rate) : 1));
      const cartTotal = Number(this.getAttribute('data-cart-total'));
      const freeShippingRemaining = cartTotal - freeShippingThreshold;

      let cartSliderWidth = 0;
      if ( freeShippingRemaining < 0 ) {
        const remaining = this._formatMoney(Math.abs(freeShippingRemaining), KROWN.settings.shop_money_format);
        // "Add £X.XX for Free Ship" — amount in big blue/bold, "Free Ship" bold.
        textEl.innerHTML =
          'Add <strong class="nt-free-ship__amount">' + remaining +
          '</strong> for <strong class="nt-free-ship__phrase">Free Delivery</strong>';
        cartSliderWidth = 100 - (Math.abs(freeShippingRemaining) * 100 / freeShippingThreshold);
      } else {
        // Threshold met — "Free Ship Unlocked!" with "Free Ship" bold.
        textEl.innerHTML = '<strong class="nt-free-ship__phrase">Free Delivery</strong> Unlocked!';
        cartSliderWidth = 100;
      }
      const slider = this.querySelector('[data-js-free-shipping-slider]');
      if (slider) slider.style.width = `${cartSliderWidth}%`;
    }

    // One helper-section fetch shared by every bucketed bar on the page (drawer + cart page
    // render up to three copies of each), reset shortly after so the next cart change re-reads.
    static fetchBuckets() {
      if (ShippingNotice._bucketsPromise) return ShippingNotice._bucketsPromise;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      ShippingNotice._bucketsPromise = fetch(root + '?section_id=helper-blanks-freeship', { credentials: 'same-origin' })
        .then((res) => res.text())
        .then((html) => {
          const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-pdp-blanks-freeship-helper]');
          if (!el) return null;
          return {
            blanksTotal: Number(el.getAttribute('data-blanks-total')) || 0,
            blanksCount: Number(el.getAttribute('data-blanks-count')) || 0,
            otherTotal: Number(el.getAttribute('data-other-total')) || 0,
            otherCount: Number(el.getAttribute('data-other-count')) || 0
          };
        })
        .catch(() => null /* keep the last rendered state */);
      setTimeout(() => { ShippingNotice._bucketsPromise = null; }, 500);
      return ShippingNotice._bucketsPromise;
    }

    _formatMoney(cents, format) {

      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || moneyFormat;

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount + KROWN.settings.iso_code;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

}
